import { useEffect, useState } from "react";
import { api, formatARS } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Search, Users, Phone, Mail, Calendar as CalIcon } from "lucide-react";

export default function Customers() {
  const [customers, setCustomers] = useState([]);
  const [q, setQ] = useState("");
  const [active, setActive] = useState(null);
  const [bookings, setBookings] = useState([]);

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/customers");
      setCustomers(data);
    })();
  }, []);

  const openCustomer = async (c) => {
    setActive(c);
    const { data } = await api.get(`/customers/${c.customer_id}/bookings`);
    setBookings(data);
  };

  const filtered = customers.filter((c) => {
    const s = q.toLowerCase();
    return !s || c.name?.toLowerCase().includes(s) || c.phone?.includes(s) || c.email?.toLowerCase().includes(s);
  });

  return (
    <div>
      <div className="mb-8">
        <div className="text-xs uppercase tracking-[0.3em] text-emerald-400 mb-2">// Base de datos</div>
        <h1 className="font-heading text-4xl sm:text-5xl tracking-tight">CLIENTES</h1>
      </div>

      <div className="relative mb-6 max-w-md">
        <Search className="absolute left-3 top-3 h-4 w-4 text-zinc-500" />
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Buscar por nombre, teléfono o email..."
          className="bg-[#141414] border-[#262626] pl-10"
          data-testid="customer-search"
        />
      </div>

      {filtered.length === 0 ? (
        <div className="bg-[#141414] border border-[#262626] rounded-lg p-16 text-center text-zinc-500" data-testid="no-customers">
          <Users className="h-12 w-12 mx-auto mb-4 text-zinc-700" />
          <div className="uppercase tracking-widest text-xs">
            {customers.length === 0 ? "Aún no hay clientes. Cuando alguien reserve, aparecerá acá." : "Sin resultados"}
          </div>
        </div>
      ) : (
        <div className="bg-[#141414] border border-[#262626] rounded-lg overflow-hidden">
          <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-3 border-b border-[#262626] text-xs uppercase tracking-[0.2em] text-zinc-500">
            <div className="col-span-4">Cliente</div>
            <div className="col-span-3">Contacto</div>
            <div className="col-span-2 text-center">Turnos</div>
            <div className="col-span-3 text-right">Total gastado</div>
          </div>
          {filtered.map((c) => (
            <button
              key={c.customer_id}
              onClick={() => openCustomer(c)}
              className="w-full text-left grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-4 px-4 md:px-6 py-4 border-b border-[#262626] last:border-b-0 hover:bg-[#1E1E1E] transition"
              data-testid={`customer-${c.customer_id}`}
            >
              <div className="md:col-span-4 flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-emerald-500/20 flex items-center justify-center text-emerald-400 font-bold shrink-0">
                  {c.name?.[0]?.toUpperCase() || "?"}
                </div>
                <div className="font-bold">{c.name}</div>
              </div>
              <div className="md:col-span-3 text-sm text-zinc-400 space-y-0.5">
                <div className="flex items-center gap-2"><Phone className="h-3 w-3" />{c.phone}</div>
                {c.email && <div className="flex items-center gap-2 truncate"><Mail className="h-3 w-3" />{c.email}</div>}
              </div>
              <div className="md:col-span-2 md:text-center">
                <Badge className="bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 uppercase tracking-widest text-xs">
                  {c.bookings_count} turnos
                </Badge>
              </div>
              <div className="md:col-span-3 md:text-right font-heading text-xl">{formatARS(c.total_spent)}</div>
            </button>
          ))}
        </div>
      )}

      <Dialog open={!!active} onOpenChange={(v) => !v && setActive(null)}>
        <DialogContent className="bg-[#141414] border-[#262626] max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading text-3xl tracking-wider">{active?.name?.toUpperCase()}</DialogTitle>
          </DialogHeader>
          {active && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="p-3 bg-[#0A0A0A] rounded border border-[#262626]">
                  <div className="text-xs uppercase tracking-widest text-zinc-500 mb-1">Teléfono</div>
                  <div>{active.phone}</div>
                </div>
                <div className="p-3 bg-[#0A0A0A] rounded border border-[#262626]">
                  <div className="text-xs uppercase tracking-widest text-zinc-500 mb-1">Email</div>
                  <div className="truncate">{active.email || "—"}</div>
                </div>
                <div className="p-3 bg-[#0A0A0A] rounded border border-[#262626]">
                  <div className="text-xs uppercase tracking-widest text-zinc-500 mb-1">Turnos confirmados</div>
                  <div className="font-heading text-2xl text-emerald-400">{active.confirmed_count}</div>
                </div>
                <div className="p-3 bg-[#0A0A0A] rounded border border-[#262626]">
                  <div className="text-xs uppercase tracking-widest text-zinc-500 mb-1">Total gastado</div>
                  <div className="font-heading text-2xl">{formatARS(active.total_spent)}</div>
                </div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-[0.2em] text-emerald-400 mb-3">// Historial de turnos</div>
                <div className="space-y-2 max-h-80 overflow-y-auto">
                  {bookings.map((b) => (
                    <div key={b.booking_id} className="flex items-center gap-3 p-3 bg-[#0A0A0A] rounded border border-[#262626] text-sm">
                      <CalIcon className="h-4 w-4 text-zinc-500" />
                      <div className="flex-1">
                        <div>{b.date} · {b.start_time} · {b.court_name}</div>
                      </div>
                      <div className="font-bold">{formatARS(b.price_ars)}</div>
                      <Badge className={`text-xs uppercase ${
                        b.status === "confirmed" ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/30" :
                        b.status === "pending" ? "bg-amber-500/10 text-amber-400 border-amber-500/30" :
                        "bg-red-500/10 text-red-400 border-red-500/30"
                      } border`}>{b.status}</Badge>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
